import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import API from "../API/Main/API";
import {
    AssignBCBAsProperties,
    AssignClinicalSupervisorProperties,
    SaveUserDataProperties,
    UpdatedUserProperties,
    UpdateUserDataProperties,
    UserPaginationProperties,
    UserProperties,
    UserTableDataProperties,
} from "../API/userAPIProperties";
import { resetState } from "../resetState";

import { PaginationProperties } from "../../shared/uiComponents/Table/tableProperties";
import { ErrorProperties } from "../API/identityAPIProperties";
import { TherapistProperties } from "../API/ClientAPIHelpers/clientProperties";
import { InsuranceAuthorizationTypeProperties, InsuranceProperties } from "../API/ClientAPIHelpers/insuranceAuthorizationProperties";

interface UserStateProperties {
    loading: boolean;
    loadingInsurance: boolean;
    loadingClinicalSupervisor: boolean;
    error: ErrorProperties;
    admins: UserTableDataProperties;
    users: UserTableDataProperties;
    bcbas: UserTableDataProperties;
    bts: UserTableDataProperties;
    clinicalSupervisors: UserTableDataProperties;
    bcbasClinicalSupervisors: Array<TherapistProperties>;
    clinicalSupervisorsBcbas: Array<UserProperties> | null;
    clinicalSupervisorsTherapists: Array<TherapistProperties>;
    user: UserProperties;
    userInsurances: Array<InsuranceProperties>;
    authorizationTypes: Array<InsuranceAuthorizationTypeProperties>;
    insurances: Array<InsuranceProperties>
    saveDisabled: boolean,
}

const initialUserState: UserStateProperties = {
    loading: false,
    loadingInsurance: false,
    loadingClinicalSupervisor: false,
    userInsurances: [],
    error: {
        status: 0,
        title: ''
    },
    users: {
        query: null,
        page: 1,
        totalNumberOfItems: 0,
        numberOfItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    clinicalSupervisorsBcbas: null,
    clinicalSupervisors: {
        query: null,
        page: 1,
        totalNumberOfItems: 0,
        numberOfItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    bcbasClinicalSupervisors: [],
    clinicalSupervisorsTherapists: [],
    admins: {
        query: null,
        page: 1,
        totalNumberOfItems: 0,
        numberOfItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    bcbas: {
        query: null,
        page: 1,
        totalNumberOfItems: 0,
        numberOfItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    bts: {
        query: null,
        page: 1,
        totalNumberOfItems: 0,
        numberOfItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    user: {
        id: "",
        firstName: "",
        lastName: "",
        fullName: "",
        patronymic: "",
        numberOfClients: 0,
        phoneNumber: "",
        address: "",
        notes: "",
        email: "",
        numberOfNotes: 0,
        role: {
            id: "",
            name: "",
            section: {
                id: 0,
                name: "",
            }
        },
        createdBy: {
            id: "",
            fullName: "",
            role: "",
            authorizationType: {
                id: 0,
                name: "",
            }
        },
        authorizationType: {
            id: 0,
            name: "",
        },
        emailConfirmed: false,
        isDeactivated: false,
        isCaseCoordinator: false,
        mbhId: "",
        caseCoordinators: [],
    },
    authorizationTypes: [],
    insurances: [],
    saveDisabled: false,
}

export const getClinicalSupervisorsTherapists = createAsyncThunk(
    'user/clinical-supervisor/therapists',
    async (userId: string, thunkAPI) => {
        const response = await API.UserAPI.clinicalSupervisor.getClinicalSupervisorsTherapists(userId);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<TherapistProperties>;
    }
)

export const getClinicalSupervisorsBCBAs = createAsyncThunk(
    'user/clinical-supervisor/bcbas',
    async (userId: string, thunkAPI) => {
        const response = await API.UserAPI.clinicalSupervisor.getClinicalSupervisorsBCBAs(userId);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<UserProperties>;
    }
)

export const getBCBAClinicalSupervisors = createAsyncThunk(
    'user/bcba/clinical-supervisors',
    async (userId: string, thunkAPI) => {
        const response = await API.UserAPI.clinicalSupervisor.getBCBAClinicalSupervisors(userId);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<TherapistProperties>;
    }
)

export const assignClinicalSupervisors = createAsyncThunk(
    'user/assign/clinical-supervisor/bcbas',
    async (data: AssignClinicalSupervisorProperties, thunkAPI) => {
        const response = await API.UserAPI.clinicalSupervisor.assignClinicalSupervisors(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const assignBcbas = createAsyncThunk(
    'user/assign/bcba/clinical-supervisors',
    async (data: AssignBCBAsProperties, thunkAPI) => {
        const response = await API.UserAPI.clinicalSupervisor.assignBCBAs(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const getAuthorizationTypes = createAsyncThunk(
    'user/authorization/types',
    async (_, thunkAPI) => {
        const response = await API.UserAPI.authorization.getAuthorizationTypes();
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<InsuranceAuthorizationTypeProperties>;
    }
)

export const getUserInsurances = createAsyncThunk(
    '/user/user/insurances',
    async (id: string, thunkAPI) => {
        const response = await API.UserAPI.insurance.getUserInsurances(id);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<InsuranceProperties>;
    }
)

export const getUsers = createAsyncThunk(
    '/user/users',
    async (data: UserPaginationProperties, thunkAPI) => {
        const response = await API.UserAPI.user.getUsers(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserTableDataProperties;
    }
)

export const getAdmins = createAsyncThunk(
    '/user/users/admin',
    async (data: UserPaginationProperties, thunkAPI) => {
        const response = await API.UserAPI.user.getUsersBySection(data, 0);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserTableDataProperties;
    }
)

export const getBCBAs = createAsyncThunk(
    '/user/users/bcba',
    async (data: PaginationProperties, thunkAPI) => {
        const response = await API.UserAPI.user.getUsersBySection(data, 1);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserTableDataProperties;
    }
)

export const getBTs = createAsyncThunk(
    '/user/users/bt',
    async (data: PaginationProperties, thunkAPI) => {
        const response = await API.UserAPI.user.getUsersBySection(data, 2);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserTableDataProperties;
    }
)

export const getClinicalSupervisors = createAsyncThunk(
    '/user/users/cs',
    async (data: PaginationProperties, thunkAPI) => {
        const response = await API.UserAPI.user.getUsersBySection(data, 3);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserTableDataProperties;
    }
)

export const addUser = createAsyncThunk(
    '/user/add',
    async (data: SaveUserDataProperties, thunkAPI) => {
        const response = await API.UserAPI.user.addUser(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserProperties;
    }
)

export const getUser = createAsyncThunk(
    '/user/user',
    async (id: string, thunkAPI) => {
        const response = await API.UserAPI.user.getUser(id);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UserProperties;
    }
)

export const updateUser = createAsyncThunk(
    '/user/update',
    async (data: UpdateUserDataProperties, thunkAPI) => {
        const response = await API.UserAPI.user.updateUser(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as UpdatedUserProperties;
    }
)

export const deleteUser = createAsyncThunk(
    '/user/delete',
    async (id: string, thunkAPI) => {
        const response = await API.UserAPI.user.deleteUser(id);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

const userSlice = createSlice({
    name: 'user',
    initialState: initialUserState,
    reducers: {
        updateSaveDisabled(state, action) {
            state.saveDisabled = action.payload;
        }
    },
    extraReducers: (builder) => {
        //getClinicalSupervisorsTherapists
        builder.addCase(getClinicalSupervisorsTherapists.pending, (state) => {
            state.loadingClinicalSupervisor = true;
        })
        builder.addCase(getClinicalSupervisorsTherapists.fulfilled, (state, action) => {
            state.loadingClinicalSupervisor = false;
            state.clinicalSupervisorsTherapists = action.payload;
        })
        builder.addCase(getClinicalSupervisorsTherapists.rejected, (state, action) => {
            state.loadingClinicalSupervisor = false;
            state.error = action.payload as ErrorProperties;
        })
        //getClinicalSupervisorsBCBAs
        builder.addCase(getClinicalSupervisorsBCBAs.pending, (state) => {
            state.loadingClinicalSupervisor = true;
        })
        builder.addCase(getClinicalSupervisorsBCBAs.fulfilled, (state, action) => {
            state.loadingClinicalSupervisor = false;
            state.clinicalSupervisorsBcbas = action.payload;
        })
        builder.addCase(getClinicalSupervisorsBCBAs.rejected, (state, action) => {
            state.loadingClinicalSupervisor = false;
            state.error = action.payload as ErrorProperties;
        })
        //getBCBAClinicalSupervisors
        builder.addCase(getBCBAClinicalSupervisors.pending, (state) => {
            state.loadingClinicalSupervisor = true;
        })
        builder.addCase(getBCBAClinicalSupervisors.fulfilled, (state, action) => {
            state.loadingClinicalSupervisor = false;
            state.bcbasClinicalSupervisors = action.payload;
        })
        builder.addCase(getBCBAClinicalSupervisors.rejected, (state, action) => {
            state.loadingClinicalSupervisor = false;
            state.error = action.payload as ErrorProperties;
        })
        //getUserInsurances
        builder.addCase(getUserInsurances.pending, (state) => {
            state.loadingInsurance = true;
        })
        builder.addCase(getUserInsurances.fulfilled, (state, action) => {
            state.loadingInsurance = false;
            state.userInsurances = action.payload;
        })
        builder.addCase(getUserInsurances.rejected, (state, action) => {
            state.loadingInsurance = false;
            state.error = action.payload as ErrorProperties;
        })
        //getAuthorizationTypes
        builder.addCase(getAuthorizationTypes.pending, (state) => {
            state.loading = true;
        })
        builder.addCase(getAuthorizationTypes.fulfilled, (state, action) => {
            state.loading = false;
            state.authorizationTypes = action.payload;
        })
        builder.addCase(getAuthorizationTypes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //get users
        builder.addCase(getUsers.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(getUsers.fulfilled, (state, action) => {
            state.loading = false;
            state.users = action.payload;
        })
        builder.addCase(getUsers.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //get admin
        builder.addCase(getAdmins.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(getAdmins.fulfilled, (state, action) => {
            state.loading = false;
            state.admins = action.payload;
        })
        builder.addCase(getAdmins.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //get BCBA
        builder.addCase(getBCBAs.pending, (state) => {
            // state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(getBCBAs.fulfilled, (state, action) => {
            // state.loading = false;
            state.bcbas = action.payload;
        })
        builder.addCase(getBCBAs.rejected, (state, action) => {
            // state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getBTs
        builder.addCase(getBTs.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(getBTs.fulfilled, (state, action) => {
            state.loading = false;
            state.bts = action.payload;
        })
        builder.addCase(getBTs.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getClinicalSsupervisors
        builder.addCase(getClinicalSupervisors.pending, (state) => {
            // state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(getClinicalSupervisors.fulfilled, (state, action) => {
            // state.loading = false;
            state.clinicalSupervisors = action.payload;
        })
        builder.addCase(getClinicalSupervisors.rejected, (state, action) => {
            // state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //get user
        builder.addCase(getUser.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(getUser.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload;
        })
        builder.addCase(getUser.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //add user
        builder.addCase(addUser.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(addUser.fulfilled, (state) => {
            state.loading = false;
        })
        builder.addCase(addUser.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //update user
        builder.addCase(updateUser.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(updateUser.fulfilled, (state) => {
            state.loading = false;
        })
        builder.addCase(updateUser.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //delete user
        builder.addCase(deleteUser.pending, (state) => {
            state.loading = true;
            state.error = initialUserState.error;
        })
        builder.addCase(deleteUser.fulfilled, (state) => {
            state.loading = false;
        })
        builder.addCase(deleteUser.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //reset state
        builder.addCase(resetState, () => initialUserState);
    }
})

export const { updateSaveDisabled } = userSlice.actions
export default userSlice.reducer;